import React from 'react';
import {StyleSheet, View, type StyleProp, type ViewStyle} from 'react-native';

import {AppButton} from '@/shared/ui/button';
import {IconBubble} from '@/shared/ui/card';
import {Icon, type IconName} from '@/shared/ui/icons';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * What a list says when it has nothing to say.
 *
 * Every empty and error state in the app used to be one line of muted 15pt text
 * floating in the middle of a blank screen, which looks indistinguishable from a
 * failed render. An icon, a sentence and — where one exists — the action that would
 * fix it, is the difference between "broken" and "nothing here yet".
 *
 * `tone` selects the wash behind the glyph: neutral for empty, destructive for an
 * error, so a failure is legible before it is read.
 */
const styles = StyleSheet.create({
  root: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing(2),
    paddingVertical: spacing(10),
    paddingHorizontal: spacing(5),
  },
  text: {alignItems: 'center', gap: spacing(1), maxWidth: 380},
  action: {marginTop: spacing(3), minWidth: 160, alignSelf: 'center'},
});

export function EmptyState({
  icon = 'feed',
  title,
  hint,
  tone = 'neutral',
  actionLabel,
  onAction,
  style,
  testID,
}: {
  icon?: IconName;
  title: string;
  hint?: string;
  tone?: 'neutral' | 'error';
  actionLabel?: string;
  onAction?: () => void;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const p = useTheme();
  const isError = tone === 'error';

  return (
    <View testID={testID} style={[styles.root, style]}>
      {/* Значок на плашке `surface-inset` с тонкой рамкой (ui-kit `emptyIcon`), а не
          в воздухе на 60% прозрачности: a faded mark on an empty panel reads as
          something that failed to load, which is the one thing an empty state must
          not look like. */}
      <IconBubble size={56} tone={isError ? p.destructiveSoft : undefined}>
        <Icon
          name={isError ? 'warning' : icon}
          size={24}
          color={isError ? p.destructive : p.mutedForeground}
        />
      </IconBubble>

      <View style={styles.text}>
        <AppText variant="heading" style={{textAlign: 'center'}}>
          {title}
        </AppText>
        {hint ? (
          <AppText variant="caption" style={{textAlign: 'center', lineHeight: 18}}>
            {hint}
          </AppText>
        ) : null}
      </View>

      {actionLabel && onAction ? (
        <AppButton
          title={actionLabel}
          tone={isError ? 'outline' : 'primary'}
          size="sm"
          onPress={onAction}
          style={styles.action}
        />
      ) : null}
    </View>
  );
}
